/**
 * SE-6490 — the pipeline card's identity + context rows (Player Card v7).
 *
 * @remarks
 * Rows 1 and 2 of the approved design:
 *
 * 1. **Identity** — avatar · player name · GPR score in a badge, right-aligned.
 * 2. **Context** — position chip, then a single muted `club · age · contract`
 *    line.
 *
 * Rendered entirely through molecules already ratified against THIS Figma
 * component: the design registry maps `Player Card v7` to `MobileTargetCardView`,
 * and `InitialsAvatar` / `RatingBadge` / `PositionBadge` / `MetaRow` are the
 * pieces SE-5522 pixel-audited and SE-5527 promoted out of it for reuse. No
 * value here is newly resolved from the design — a Figma literal that has no
 * ratified mapping is a stop, never a guess.
 *
 * **What this row set deliberately no longer renders** (SE-6490, product
 * decision 2026-07-31): the `#N` rank prefix, the agency name, the bold role
 * archetype, and the `N days in stage` body text. All of them are reachable
 * from the preview drawer (SE-5494); none was silently dropped. Days-in-stage
 * still drives the stale flag, which moved to the signals row.
 *
 * **The wrap/overlap defect this fixes:** the previous context row laid club,
 * archetype and position out as four unconstrained `Text` nodes, so a long role
 * (`RIGHT INSIDE FORWARD`) wrapped onto a second line and collided with the
 * position. `MetaRow` is a single `numberOfLines={1}` line and `PositionBadge`
 * is `shrink-0`, so the row now truncates instead of colliding.
 * @module components/organisms/custom/ui/PlayerCardHeader/PlayerCardHeaderContainer
 */
import React, { useCallback } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import MetaRow from "@/components/molecules/MetaRow";
import PlayerAvatar from "@/components/molecules/PlayerAvatar";
import PositionBadge from "@/components/molecules/PositionBadge";
import RatingBadge from "@/components/molecules/RatingBadge";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/** Props for PlayerCardHeader */
interface PlayerCardHeaderProps {
  /** Player display name */
  readonly playerName: string;
  /** Name of the player's current club */
  readonly teamName: string;
  /** Player's age in whole years; absent drops the segment */
  readonly age?: number;
  /** Months remaining on contract; `null` drops the segment */
  readonly contractMonths: number | null;
  /** Gemini Performance Rating; absent renders no badge */
  readonly gpr?: number;
  /**
   * SE-5492: canonical player image URL. Null for every player today
   * (SE-5538 is schema plumbing only) → the initials fallback renders.
   */
  readonly imageUrl?: string | null;
  /**
   * SE-5492: explicit position — distinct from the ROLE archetype. Omitted
   * when the player has no position source, which drops the chip.
   */
  readonly position?: string;
}

/**
 * The pipeline card's identity and context rows.
 * @param root0 - Component props
 * @param root0.playerName - Player display name
 * @param root0.teamName - Club name
 * @param root0.age - Player age in whole years
 * @param root0.contractMonths - Months remaining on contract
 * @param root0.gpr - Gemini Performance Rating
 * @param root0.imageUrl - Canonical player image; null today → initials fallback
 * @param root0.position - Explicit position (not the role archetype)
 */
const PlayerCardHeader = ({
  playerName,
  teamName,
  age,
  contractMonths,
  gpr,
  imageUrl,
  position,
}: PlayerCardHeaderProps) => {
  // The board card binds a single primary position, so the badge never enters
  // its multi-position branch and this handler is unreachable. It is required
  // by the shared molecule's contract; wiring a navigation here would invent
  // behaviour the design does not show.
  const handlePositionsPress = useCallback(() => undefined, []);

  return (
    <>
      {/* Row 1: avatar · name · GPR badge */}
      <Stack direction="horizontal" className="items-center gap-2">
        {/* SE-5492: renders the initials circle for every player today —
            Player.imageUrl is null until SE-5538's follow-up populates it. */}
        <PlayerAvatar
          playerName={playerName}
          imageUrl={imageUrl}
          testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_AVATAR}
        />
        <Text
          variant="title-sm"
          numberOfLines={1}
          className="flex-1 text-content-primary"
        >
          {playerName}
        </Text>
        {gpr != null ? (
          <RatingBadge
            value={String(Math.round(gpr))}
            testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_GPR}
          />
        ) : null}
      </Stack>

      {/* Row 2: position chip · club · age · contract */}
      <Stack direction="horizontal" className="items-center gap-2">
        {/* The chip's own `testID` addresses its multi-position TAP TARGET and
            is deliberately absent on the plain single-position branch, which
            is the only branch the board card uses. The card therefore carries
            its own identity on a wrapper — rendered only when there IS a
            position, so "no position" still reads as `toHaveCount(0)` rather
            than an empty element that passes for the wrong reason. */}
        {position ? (
          <Box testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_POSITION}>
            <PositionBadge
              position={position}
              positionExtraCount={0}
              onPositionsPress={handlePositionsPress}
              accessibilityLabel={`Position for ${playerName}`}
            />
          </Box>
        ) : null}
        <MetaRow
          testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_META}
          segments={[
            teamName,
            age != null ? `${age}y` : null,
            contractMonths !== null ? `Contract ${contractMonths}mo` : null,
          ]}
        />
      </Stack>
    </>
  );
};

export default PlayerCardHeader;
